import { useNavigate } from 'react-router-dom';
import { RoutesList } from '@animal-nuisance/routes/Routes';
import { YourDetailsTemplate } from '@templates';
import { ChangeEvent, useEffect, useState } from 'react';
import { basename } from '@animal-nuisance/routes/basename';
import { useAnimalNuisanceStore } from '@animal-nuisance/hooks/useAnimalNuisanceStore';
import {
  handleLoginPageEvent,
  handleLoginRequest,
  LoginRequestConfig,
  msalInstance,
  parseZodErrors,
  yourDetailsSchema,
} from '@utils';
import { useUserData } from '@hooks';
import { ErrorMessages } from '@types';
import { useHandleSubmit } from '@animal-nuisance/hooks/useHandleSubmit';
import { ZodError } from 'zod';
import { UserDetailsType } from '@shared/types';
import { AnimalNuisancePayload } from '../../payload/Payload';

export const YourDetailsPage = () => {
  const navigate = useNavigate();
  const {
    data: { userDetails, caseReferenceId },
    update,
  } = useAnimalNuisanceStore();

  const [formData, setFormData] = useState<UserDetailsType>(userDetails);
  const [crmId, setCrmId] = useState<string>('');
  const [idProvider, setIdProvider] = useState<string>('');
  const { isLoggedInUser, contactData, isLoading, isFetching } =
    useUserData(crmId);
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const isLoadingUserData = isLoading || isFetching;
  const { mutateAsync: submitForm, isPending } = useHandleSubmit();
  const [submitLoading, setSubmitLoading] = useState(false);
  const errorPath = 'yourDetails';

  useEffect(() => {
    if (!crmId) {
      handleLoginPageEvent(msalInstance, setCrmId, setIdProvider);
    }

    if (contactData) {
      const data = {
        email: contactData.emailaddress1 || '',
        firstName: contactData.firstname || '',
        lastName: contactData.lastname || '',
        contactId: crmId,
      };
      setFormData(data);
    }
  }, [contactData, crmId, idProvider]);

  const handleSubmit = async () => {
    const { firstName, lastName, email, contactId } = formData;
    try {
      const validatedData = yourDetailsSchema.parse({
        firstName,
        lastName,
        email,
      });
      update('userDetails', {
        ...validatedData,
        contactId,
      });
      const payload = AnimalNuisancePayload();
      await submitForm(payload);
      navigate(RoutesList.CONFIRMATION);
    } catch (error) {
      if (error instanceof ZodError) {
        const newErrorMessages = parseZodErrors(error);
        setErrorMessages(newErrorMessages);
      } else {
        setErrorMessages([
          {
            message: (error as Error).message,
            path: errorPath,
          },
        ]);
      }
      throw new Error();
    }
  };

  const handleNext = async () => {
    if (caseReferenceId) {
      setErrorMessages([
        {
          path: errorPath,
          message:
            'You have already submitted this report. Please return to the homepage.',
        },
      ]);
      return;
    }
    setSubmitLoading(true);
    try {
      await handleSubmit();
      navigate(RoutesList.CONFIRMATION);
      // add this to allow popstate event listener to be triggered
      window.history.pushState({}, '', basename + RoutesList.CONFIRMATION);
    } catch (error) {
      throw new Error();
    } finally {
      setSubmitLoading(false);
    }
  };

  const loginConfig: LoginRequestConfig = {
    msal: msalInstance,
    navigate: () => navigate(RoutesList.YOUR_DETAILS, { replace: true }),
  };

  const handleLogin = async () => {
    await handleLoginRequest(loginConfig);
  };

  const buttonPrevious = {
    disabled: isPending || submitLoading,
    onClick: () => {
      navigate(-1);
    },
  };

  const buttonNext = {
    label: 'Submit',
    loading: isPending || submitLoading,
    onClick: handleNext,
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const updatedData = {
      ...formData,
      [e.target.name]: e.target.value,
    };
    setFormData(updatedData);
    update('userDetails', updatedData);
    setErrorMessages([]);
  };

  return (
    <YourDetailsTemplate
      isLoadingUserData={isLoadingUserData}
      isLoggedInUser={isLoggedInUser}
      userData={formData}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      errorMessages={errorMessages}
      handleLogin={handleLogin}
      onChange={handleChange}
      idProvider={idProvider}
    />
  );
};
